import React from 'react';

// Basic shimmer bar
export const SkeletonBar: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton-gradient rounded ${className}`} />
);

// Card mimicking a GuildCard row
export const SkeletonGuildCard: React.FC = () => (
  <div className="relative bg-surface-container-low rounded-lg flex items-center p-4">
    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-secondary-container/40" />
    <div className="w-12 h-12 rounded-full skeleton-gradient flex-shrink-0" />
    <div className="ml-4 min-w-0 flex-1 space-y-2">
      <SkeletonBar className="h-4 w-2/3" />
      <SkeletonBar className="h-3 w-24" />
    </div>
  </div>
);

// Grid of guild card skeletons matching GuildSelector layout
export const SkeletonGuildGrid: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="max-w-6xl mx-auto">
    <div className="mb-8 px-1 flex items-start justify-between gap-4">
      <div className="space-y-3">
        <SkeletonBar className="h-8 w-64" />
        <SkeletonBar className="h-3 w-80" />
      </div>
      <SkeletonBar className="h-9 w-32 rounded-lg" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonGuildCard key={i} />
      ))}
    </div>
  </div>
);

// Per-server summary card skeleton (used by Dashboard)
export const SkeletonServerSummaryCard: React.FC = () => (
  <div className="bg-surface-container-low rounded-lg p-5 space-y-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full skeleton-gradient flex-shrink-0" />
      <SkeletonBar className="h-4 w-40" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <SkeletonBar className="h-6 w-10" />
        <SkeletonBar className="h-3 w-16" />
      </div>
      <div className="space-y-2">
        <SkeletonBar className="h-6 w-10" />
        <SkeletonBar className="h-3 w-16" />
      </div>
    </div>
  </div>
);

// Dashboard skeleton — header, recent activity strip, server grid
export const SkeletonDashboard: React.FC = () => (
  <div className="max-w-6xl mx-auto">
    <div className="mb-8 px-1 space-y-3">
      <SkeletonBar className="h-8 w-48" />
      <SkeletonBar className="h-3 w-80" />
    </div>

    <div className="mb-8 bg-surface-container-low rounded-lg p-5 space-y-3">
      <SkeletonBar className="h-3 w-32" />
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full skeleton-gradient" />
            <SkeletonBar className="h-3 flex-1" />
            <SkeletonBar className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonServerSummaryCard key={i} />
      ))}
    </div>
  </div>
);
